import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import './RejectionChart.css';

/**
 * RejectionChart Component
 * Horizontal bar chart showing where we lose candidates
 */
function RejectionChart({ data, loading }) {
    if (loading) {
        return (
            <div className="chart-container chart-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="chart-container chart-empty">
                <p>No rejection data available</p>
            </div>
        );
    }

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const d = payload[0].payload;
            return (
                <div className="chart-tooltip">
                    <p className="chart-tooltip-label">{d.reason}</p>
                    <p className="chart-tooltip-item">
                        Count: <strong>{d.count}</strong> candidates
                    </p>
                    <p className="chart-tooltip-item">
                        Avg Days to Reject: <strong>{d.avgDaysToReject}</strong> days
                    </p>
                    <p className={`chart-tooltip-item ${d.isControllable ? 'controllable' : 'uncontrollable'}`}>
                        {d.isControllable ? 'Controllable' : 'Uncontrollable'}
                    </p>
                </div>
            );
        }
        return null;
    };

    // Calculate total for percentage
    const total = data.reduce((sum, d) => sum + d.count, 0);
    const chartData = data.map(d => ({
        ...d,
        percentage: ((d.count / total) * 100).toFixed(1),
        shortReason: d.reason.length > 25 ? d.reason.substring(0, 22) + '...' : d.reason
    }));

    return (
        <div className="chart-container rejection-chart">
            <div className="chart-header">
                <h3 className="chart-title">Where We Lose People</h3>
                <div className="rejection-legend">
                    <span className="rejection-legend-item">
                        <span className="legend-bar controllable"></span>
                        Controllable
                    </span>
                    <span className="rejection-legend-item">
                        <span className="legend-bar uncontrollable"></span>
                        Uncontrollable
                    </span>
                </div>
            </div>

            <ResponsiveContainer width="100%" height={280}>
                <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 10, right: 60, left: 140, bottom: 10 }}
                >
                    <XAxis type="number" hide />
                    <YAxis
                        type="category"
                        dataKey="shortReason"
                        tick={{ fontSize: 12, fill: '#2C3E50' }}
                        axisLine={false}
                        tickLine={false}
                        width={130}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                        dataKey="count"
                        radius={[0, 4, 4, 0]}
                        barSize={20}
                    >
                        {chartData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={entry.isControllable ? '#F39C12' : '#95A5A6'}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>

            {/* Summary stats */}
            <div className="rejection-summary">
                <div className="rejection-stat">
                    <span className="stat-value">
                        {chartData.filter(d => d.isControllable).reduce((sum, d) => sum + d.count, 0)}
                    </span>
                    <span className="stat-label">Controllable Losses</span>
                </div>
                <div className="rejection-stat">
                    <span className="stat-value">
                        {chartData.filter(d => !d.isControllable).reduce((sum, d) => sum + d.count, 0)}
                    </span>
                    <span className="stat-label">Uncontrollable Losses</span>
                </div>
                <div className="rejection-stat">
                    <span className="stat-value">
                        {Math.round(chartData.reduce((sum, d) => sum + d.avgDaysToReject * d.count, 0) / total)}
                    </span>
                    <span className="stat-label">Avg Days to Reject</span>
                </div>
            </div>
        </div>
    );
}

export default RejectionChart;
